<template>
  <PortalLayout section="Prepare" hidePortalSelector hideHeader>
    <div class="pb-4">
      <BackButton :to="'../test/' + trainingQuestion.testId">Back to Test </BackButton>
    </div>
    <div v-if="isPropertyLoading || isQuestionLoading"><Loading /></div>
    <div v-else>
      <h1 class="py-3 mb-6 border-b flex">
        <div class="text-xl flex-grow">
          <div class="font-bold">Edit Question</div>
          <div>
            {{ trainingQuestion?.text }}
          </div>
        </div>
        <EditButton @click="editQuestionClick">Edit</EditButton>
      </h1>
      <h2 class="font-medium">Explanation</h2>
      <div>{{ trainingQuestion?.explanation }}</div>
      <br />
      <h2 class="font-medium">Answers</h2>
      <div v-if="trainingQuestion.answers && trainingQuestion.answers.length > 0">
        <AddButton class="mt-2" @click="addAnswerClick">Add Answer</AddButton>
        <div
          v-for="answer in trainingQuestion.answers"
          :key="answer.questionId"
          class="py-2 px-4 my-2 border-t flex cursor-pointer hover:bg-blue-100"
          :class="answer.isCorrect ? 'bg-green-200' : 'bg-gray-100'"
          @click="editAnswerClick(answer)"
        >
          <div class="py-1 px-2 flex-grow">
            {{ answer.text }} <span v-if="answer.isCorrect">(correct)</span>
          </div>
          <IHeroiconsOutlinePencil class="mt-1.5" />
        </div>
      </div>
      <div class="m-2" v-else>
        <span>-- No Answers found for this Question --</span>
      </div>
      <AddButton @click="addAnswerClick">Add Answer</AddButton>
      <EditPopup
        entity-name="Answer"
        :is-visible="isAnswerPopupVisible"
        :is-edit-mode="isAnswerEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isAnswerPopupVisible = false"
        @save="saveAnswerClick"
        @delete="deleteAnswerClick"
      >
        <BaseInput label="Text" v-model="answerText" :error="errorsAnswer.answerText" />
        <div class="h-3" />
        <BaseCheckbox label="Correct Answer" v-model="answerIsCorrect" />
      </EditPopup>
      <EditPopup
        entity-name="Question"
        :is-visible="isQuestionPopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isQuestionPopupVisible = false"
        @save="saveQuestionClick"
      >
        <BaseInput
          label="Text"
          v-model="questionText"
          :error="errorsQuestion.questionText"
        />
        <div class="h-3" />
        <BaseInput
          label="Explanation"
          v-model="questionExplanation"
          :error="errorsQuestion.questionExplanation"
        />
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { QuestionDTO, AnswerDTO } from "../../store/generatedFetchClient";
import { useTrainingStore } from "../../store/trainingStore";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();
const route = useRoute();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var isQuestionLoading = ref(true);
var property = computed(() => bldgStore.property);

let trainingQuestion = computed(() => trainingStore.selectedQuestion);

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  isQuestionLoading.value = true;
  if (property.value?.propertyId) {
    if (route.params.questionId && typeof route.params.questionId === "string") {
      let questionId = Number.parseInt(route.params.questionId);
      await trainingStore.getQuestion(questionId);
    }
  }
  isQuestionLoading.value = false;
}

// Popup stuff
let saveError = ref("");

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

// Question Edit
let isQuestionPopupVisible = ref(false);

const { handleSubmit: handleSubmitQuestion, errors: errorsQuestion } = useForm({
  validationSchema: yup.object({
    questionText: yup.string().required().max(256).label("Text"),
    questionExplanation: yup.string().required().max(1024).label("Explanation"),
  }),
  initialValues: {
    questionText: "",
    questionExplanation: "",
  },
});

const { value: questionText } = useField<string>("questionText");
const { value: questionExplanation } = useField<string>("questionExplanation");

let saveQuestionClick = handleSubmitQuestion(onValidSubmitQuestion, onInvalidSubmit);

async function onValidSubmitQuestion() {
  let newQuestion = {
    questionId: trainingQuestion.value.questionId,
    testId: trainingQuestion.value.testId,
    text: questionText.value,
    explanation: questionExplanation.value,
  } as QuestionDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveQuestion(newQuestion);
    if (!saveError.value) {
      trainingQuestion.value.text = questionText.value;
      trainingQuestion.value.explanation = questionExplanation.value;
      isQuestionPopupVisible.value = false;
    }
  }
}

// Answer Edit
let isAnswerPopupVisible = ref(false);
let isAnswerEditing = ref(false);

const { handleSubmit: handleSubmitAnswer, errors: errorsAnswer } = useForm({
  validationSchema: yup.object({
    answerText: yup.string().required().max(512).label("Text"),
  }),
  initialValues: {
    answerText: "",
  },
});

let answerId = ref(0);
const { value: answerText } = useField<string>("answerText");
let answerIsCorrect = ref(false);

let saveAnswerClick = handleSubmitAnswer(onValidSubmitAnswer, onInvalidSubmit);

async function onValidSubmitAnswer() {
  let newAnswer = {
    answerId: answerId.value,
    questionId: trainingQuestion.value.questionId,
    text: answerText.value,
    isCorrect: answerIsCorrect.value,
  } as AnswerDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveAnswer(newAnswer);
    if (!saveError.value) {
      await trainingStore.getQuestion(trainingQuestion.value.questionId);
      isAnswerPopupVisible.value = false;
    }
  }
}

let editQuestionClick = function () {
  questionText.value = trainingQuestion.value.text;
  questionExplanation.value = trainingQuestion.value.explanation;

  saveError.value = "";

  isQuestionPopupVisible.value = true;
};

let addAnswerClick = function () {
  answerId.value = 0;
  answerText.value = "";
  answerIsCorrect.value = false;

  saveError.value = "";

  isAnswerEditing.value = false;
  isAnswerPopupVisible.value = true;
};

let editAnswerClick = function (selectedAnswer: AnswerDTO) {
  answerId.value = selectedAnswer.answerId ?? 0;
  answerText.value = selectedAnswer.text ?? "";
  answerIsCorrect.value = selectedAnswer.isCorrect ?? false;

  saveError.value = "";

  isAnswerEditing.value = true;
  isAnswerPopupVisible.value = true;
};

async function deleteAnswerClick() {
  if (answerId.value) {
    console.log(`Deleting Answer ${answerId.value}`);
    saveError.value = await trainingStore.deleteAnswer(answerId.value);
    if (!saveError.value) {
      await trainingStore.getQuestion(trainingQuestion.value.questionId);
      isAnswerPopupVisible.value = false;
    }
  }
}
</script>
